// SPEC-OAUTH-NATIVE — generic ASWebAuthenticationSession wrapper.
//
// Why this exists: SFSafariViewController (Capacitor's `@capacitor/browser`)
// does NOT reliably follow custom-scheme redirects on iOS 17+. Tap "Continue
// with Google" → browser → google.com OAuth → server 302 to
// `com.usetenon.app://auth?token=…` → Safari shows a 0-byte "google" data
// file with an "Open in…" picker instead of bouncing the URL back to the app.
//
// ASWebAuthenticationSession is Apple's purpose-built API for OAuth flows
// with custom callback schemes. It captures the callback URL directly and
// returns it to the iOS plugin, which forwards it to this JS wrapper. No
// Safari, no custom-scheme bounce.
//
// Backed by `OAuthSessionPlugin.swift` in the App target, registered with
// the Capacitor bridge via AppBridgeViewController (Capacitor 8
// SwiftPM custom plugin pattern).

import { Capacitor, registerPlugin } from '@capacitor/core'

interface OAuthSessionPlugin {
  start(opts: { authURL: string; callbackScheme: string }): Promise<{ callbackURL: string }>
}

const OAuthSession = registerPlugin<OAuthSessionPlugin>('OAuthSession')

/**
 * Run an OAuth flow via ASWebAuthenticationSession. The auth URL should be a
 * full-form OAuth init URL that eventually redirects to a `callbackScheme://`
 * URL. The promise resolves with the callback URL when the server redirects
 * to it, or rejects with `cancelled` if the user dismisses the sheet.
 *
 * Returns `null` when the native ASWebAuthenticationSession plugin isn't
 * available — i.e. on web AND on Android (OAuthSession is iOS-only). Callers
 * fall back to `Browser.open()` (Chrome Custom Tabs on Android) + the
 * `appUrlOpen` deep-link handler, or `window.location.href` on web.
 */
export async function runNativeOAuth(
  authURL: string,
  callbackScheme: string,
): Promise<{ callbackURL: string } | null> {
  // Only iOS ships OAuthSessionPlugin.swift. On Android, calling it throws
  // ("not implemented"), which surfaced as a "Google sign-in failed" toast;
  // returning null routes Android to the Custom Tabs fallback instead.
  if (Capacitor.getPlatform() !== 'ios') return null
  return OAuthSession.start({ authURL, callbackScheme })
}

/**
 * Parse the params out of the callback URL returned by the server-side OAuth
 * callback.
 *
 * AUTH-02 (#728): the backend sends `?code=…` — a short-lived single-use code
 * the app trades for tokens via `auth.nativeExchange`, proving possession of
 * the PKCE verifier.
 *
 * AUTH-02c Part 2 (#1520): the legacy `token`/`refresh_token` shape is RETIRED
 * — the server no longer emits raw tokens to the custom scheme, so we no longer
 * parse (or trust) them. A native build predating the code flow now gets
 * `?error=upgrade_required` instead.
 */
export function parseOAuthCallback(callbackURL: string): {
  code: string | null
  next: string | null
  error: string | null
} {
  try {
    const url = new URL(callbackURL)
    return {
      code: url.searchParams.get('code'),
      next: url.searchParams.get('next'),
      error: url.searchParams.get('error'),
    }
  } catch {
    return { code: null, next: null, error: 'parse_failed' }
  }
}
